// src/components/ai/FlowSenseAIExplainer.js
// ─────────────────────────────────────────────────────────────────────────────
// --- UPDATE (FlowSense AI Explainer) — NEW FILE ------------------------------------------------
// "Explain this" panel used across the analytics and routing screens.
//
//   <FlowSenseAIExplainer
//      module="assignment_rules"           // sent to /flowsense-ai/ask as the module hint
//      title="Why are jobs routed this way?"
//      facts={{ ... }}                      // the real data the screen already loaded
//      findings={[{ severity:'high'|'medium'|'info'|'ok', text, evidence }]}
//      questions={['What should I fix first?', ...]}
//      defaultOpen compact isLoading />
//
// Two layers, always labelled:
//   1. FlowSense rules — findings the screen computes from its own backend / ML data, instantly,
//      even when the AI is offline or the user's role cannot use it.
//   2. FlowSense AI — a plain-language explanation from the language model, built only from
//      `facts` plus the server's own snapshot. Shows the model, time taken and whether it was cached.
// Nothing here invents a number: findings only quote values passed in.
// --- END UPDATE ----------------------------------------------------------------------------------
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Badge, Box, Button, Collapse, Flex, HStack, Icon, IconButton, Input, InputGroup,
    InputRightElement, Skeleton, Spinner, Text, Tooltip, VStack, Wrap, WrapItem,
    useClipboard, useColorModeValue,
} from '@chakra-ui/react';
import {
    MdAutoAwesome, MdCheckCircle, MdContentCopy, MdErrorOutline, MdExpandLess, MdExpandMore,
    MdInfoOutline, MdRefresh, MdRule, MdSend, MdWarningAmber, MdCloudOff,
} from 'react-icons/md';
import { askFlowSenseAi, classifyAiError } from '../../services/flowsenseAiService';

const SEV = {
    high: { icon: MdErrorOutline, color: 'red.400', scheme: 'red', label: 'High' },
    critical: { icon: MdErrorOutline, color: 'red.400', scheme: 'red', label: 'Critical' },
    medium: { icon: MdWarningAmber, color: 'orange.400', scheme: 'orange', label: 'Watch' },
    warning: { icon: MdWarningAmber, color: 'orange.400', scheme: 'orange', label: 'Watch' },
    info: { icon: MdInfoOutline, color: 'blue.400', scheme: 'blue', label: 'Info' },
    ok: { icon: MdCheckCircle, color: 'green.400', scheme: 'green', label: 'OK' },
};
const ORDER = { critical: 0, high: 0, medium: 1, warning: 1, info: 2, ok: 3 };

/** Helpers screens use to turn API arrays into small, real facts. */
export const pickLabel = (row) => row?.name ?? row?.label ?? row?.stage ?? row?.status ?? row?.bucket ?? row?.type ?? row?.age_bucket ?? row?.category ?? null;
export const pickValue = (row) => {
    for (const k of ['count', 'value', 'total', 'work_orders', 'num', 'amount']) {
        const v = Number(row?.[k]);
        if (row && row[k] !== undefined && row[k] !== null && Number.isFinite(v)) return v;
    }
    return null;
};
/** [{label, value}] from an API series (unknown shape tolerated); empty when nothing numeric. */
export const summarizeSeries = (rows, limit = 12) => (Array.isArray(rows) ? rows : [])
    .map((r) => ({ label: pickLabel(r), value: pickValue(r) }))
    .filter((r) => r.label !== null && r.value !== null)
    .slice(0, limit);

const FindingRow = memo(({ f }) => {
    const s = SEV[f.severity] || SEV.info;
    const muted = useColorModeValue('gray.500', 'gray.400');
    return (
        <HStack align="start" spacing={2.5}>
            <Icon as={s.icon} color={s.color} boxSize={4} mt="2px" flexShrink={0} />
            <Box flex={1} minW={0}>
                <Text fontSize="sm" lineHeight="short">{f.text}</Text>
                {f.evidence && <Text fontSize="xs" color={muted} mt={0.5}>{f.evidence}</Text>}
            </Box>
        </HStack>
    );
});

const EMPTY = Object.freeze({});
const NONE = Object.freeze([]);

const FlowSenseAIExplainer = ({
    module, title, subtitle, facts = EMPTY, findings = NONE, questions = NONE, defaultQuestion,
    defaultOpen = false, compact = false, isLoading = false, autoAsk = false,
}) => {
    const [open, setOpen] = useState(!!defaultOpen);
    const [question, setQuestion] = useState('');
    const [asking, setAsking] = useState(false);
    const [answer, setAnswer] = useState(null);
    const [askedQ, setAskedQ] = useState(null);
    const [err, setErr] = useState(null);
    const abortRef = useRef(null);
    const autoDone = useRef(false);
    const { onCopy, hasCopied, setValue: setCopyValue } = useClipboard('');
    useEffect(() => { if (typeof setCopyValue === 'function') setCopyValue(answer?.answer || ''); }, [answer, setCopyValue]);

    const cardBg = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('purple.100', 'purple.800');
    const headBg = useColorModeValue('linear(to-r, purple.50, blue.50)', 'linear(to-r, purple.900, blue.900)');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const answerBg = useColorModeValue('purple.50', 'whiteAlpha.100');
    const inputBg = useColorModeValue('white', 'gray.700');

    const sorted = useMemo(() => (findings || [])
        .filter((f) => f && f.text)
        .sort((a, b) => (ORDER[a.severity] ?? 2) - (ORDER[b.severity] ?? 2)), [findings]);
    const counts = useMemo(() => ({
        high: sorted.filter((f) => ['high', 'critical'].includes(f.severity)).length,
        medium: sorted.filter((f) => ['medium', 'warning'].includes(f.severity)).length,
    }), [sorted]);

    useEffect(() => () => abortRef.current?.abort?.(), []);

    const ask = useCallback(async (q) => {
        const text = String(q || question || defaultQuestion || '').trim();
        if (!text || asking) return;
        abortRef.current?.abort?.();
        const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
        abortRef.current = ctrl;
        setAsking(true);
        setErr(null);
        setAskedQ(text);
        try {
            const data = await askFlowSenseAi({
                question: text,
                module,
                signal: ctrl?.signal,
                context: {
                    screen: title,
                    facts: facts || {},
                    rule_findings: sorted.slice(0, 12).map((f) => `${f.severity}: ${f.text}${f.evidence ? ` (${f.evidence})` : ''}`),
                    instructions: 'Explain using only these facts and the live snapshot. Quote numbers exactly; say when data is missing.',
                },
            });
            setAnswer(data);
            setQuestion('');
        } catch (e) {
            if (e?.name === 'AbortError') return;
            setErr(classifyAiError(e));
        } finally {
            setAsking(false);
        }
    }, [question, defaultQuestion, asking, module, title, facts, sorted]);

    useEffect(() => {
        if (autoAsk && open && !autoDone.current && !isLoading && defaultQuestion) {
            autoDone.current = true;
            ask(defaultQuestion);
        }
    }, [autoAsk, open, isLoading, defaultQuestion, ask]);

    return (
        <Box bg={cardBg} borderWidth="1px" borderColor={border} borderRadius="xl" overflow="hidden" boxShadow="sm">
            <Flex bgGradient={headBg} px={compact ? 3 : 4} py={compact ? 2 : 3} align="center" justify="space-between"
                cursor="pointer" onClick={() => setOpen((o) => !o)} role="button" aria-expanded={open}>
                <HStack spacing={3} minW={0}>
                    <Box p={1.5} borderRadius="lg" bgGradient="linear(to-br, purple.500, blue.500)" flexShrink={0}>
                        <Icon as={MdAutoAwesome} color="white" boxSize={compact ? 3.5 : 4} />
                    </Box>
                    <Box minW={0}>
                        <Text fontWeight="800" fontSize={compact ? 'sm' : 'md'} noOfLines={1}>{title || 'FlowSense AI explainer'}</Text>
                        {!compact && <Text fontSize="xs" color={muted} noOfLines={1}>{subtitle || 'Rule findings from live data, plus a plain-language AI explanation'}</Text>}
                    </Box>
                </HStack>
                <HStack spacing={2} flexShrink={0}>
                    {isLoading ? <Spinner size="xs" color="purple.400" /> : (
                        <>
                            {counts.high > 0 && <Badge colorScheme="red" borderRadius="full">{counts.high} high</Badge>}
                            {counts.medium > 0 && <Badge colorScheme="orange" borderRadius="full">{counts.medium} watch</Badge>}
                            {counts.high + counts.medium === 0 && sorted.length > 0 && <Badge colorScheme="green" borderRadius="full">no issues</Badge>}
                        </>
                    )}
                    <Icon as={open ? MdExpandLess : MdExpandMore} boxSize={5} color={muted} />
                </HStack>
            </Flex>

            <Collapse in={open} animateOpacity>
                <VStack align="stretch" spacing={4} p={compact ? 3 : 4}>
                    {/* layer 1 — rules */}
                    <Box>
                        <HStack spacing={1.5} mb={2}>
                            <Icon as={MdRule} color={muted} boxSize={3.5} />
                            <Text fontSize="2xs" fontWeight="800" color={muted} textTransform="uppercase" letterSpacing="wider">FlowSense rules · live data</Text>
                        </HStack>
                        {isLoading ? (
                            <VStack align="stretch" spacing={2}><Skeleton h="14px" /><Skeleton h="14px" w="70%" /></VStack>
                        ) : sorted.length === 0 ? (
                            <Text fontSize="sm" color={muted}>Nothing to flag from the data on this screen.</Text>
                        ) : (
                            <VStack align="stretch" spacing={2.5}>
                                {sorted.slice(0, compact ? 4 : 8).map((f, i) => <FindingRow key={`${i}-${f.text}`} f={f} />)}
                                {sorted.length > (compact ? 4 : 8) && <Text fontSize="xs" color={muted}>+{sorted.length - (compact ? 4 : 8)} more</Text>}
                            </VStack>
                        )}
                    </Box>

                    {/* layer 2 — AI */}
                    <Box>
                        <HStack spacing={1.5} mb={2}>
                            <Icon as={MdAutoAwesome} color="purple.400" boxSize={3.5} />
                            <Text fontSize="2xs" fontWeight="800" color={muted} textTransform="uppercase" letterSpacing="wider">Ask FlowSense AI</Text>
                        </HStack>
                        {Array.isArray(questions) && questions.length > 0 && (
                            <Wrap spacing={2} mb={2}>
                                {questions.slice(0, 4).map((q) => (
                                    <WrapItem key={q}>
                                        <Button size="xs" variant="outline" colorScheme="purple" borderRadius="full" fontWeight="600"
                                            onClick={() => ask(q)} isDisabled={asking || isLoading}>{q}</Button>
                                    </WrapItem>
                                ))}
                            </Wrap>
                        )}
                        <InputGroup size="sm">
                            <Input value={question} onChange={(e) => setQuestion(e.target.value)} maxLength={1000}
                                onKeyDown={(e) => { if (e.key === 'Enter') ask(); }}
                                placeholder={defaultQuestion || 'Ask about what you see on this screen…'}
                                bg={inputBg} borderRadius="lg" pr="2.5rem" isDisabled={isLoading} />
                            <InputRightElement>
                                <IconButton size="xs" variant="ghost" colorScheme="purple" aria-label="Ask FlowSense AI"
                                    icon={asking ? <Spinner size="xs" /> : <Icon as={MdSend} />}
                                    onClick={() => ask()} isDisabled={asking || isLoading || !(question || defaultQuestion)} />
                            </InputRightElement>
                        </InputGroup>

                        {err && (
                            <HStack mt={3} p={3} borderRadius="lg" bg={answerBg} spacing={2} align="start">
                                <Icon as={err.kind === 'offline' || err.kind === 'network' ? MdCloudOff : MdInfoOutline} color="orange.400" mt="2px" />
                                <Box flex={1}>
                                    <Text fontSize="sm" fontWeight="600">{err.message}</Text>
                                    <Text fontSize="xs" color={muted}>
                                        {err.kind === 'forbidden' ? 'The rule findings above come from the same data.'
                                            : err.retryInS ? `Try again in about ${err.retryInS}s — the rule findings above still apply.`
                                                : 'The rule findings above still apply.'}
                                    </Text>
                                </Box>
                                {['offline', 'busy', 'cooldown', 'network', 'unknown'].includes(err.kind) && askedQ && (
                                    <Tooltip label="Try again" hasArrow>
                                        <IconButton size="xs" variant="ghost" icon={<Icon as={MdRefresh} />} aria-label="Retry" onClick={() => ask(askedQ)} />
                                    </Tooltip>
                                )}
                            </HStack>
                        )}

                        {asking && !answer && (
                            <VStack align="stretch" spacing={2} mt={3}><Skeleton h="12px" /><Skeleton h="12px" /><Skeleton h="12px" w="60%" /></VStack>
                        )}

                        {answer && (
                            <Box mt={3} p={3} borderRadius="lg" bg={answerBg} opacity={asking ? 0.6 : 1} transition="opacity 0.2s">
                                {askedQ && <Text fontSize="xs" color={muted} mb={1.5} fontStyle="italic">“{askedQ}”</Text>}
                                <Text fontSize="sm" whiteSpace="pre-wrap" lineHeight="tall">{answer.answer}</Text>
                                <HStack mt={2} spacing={2} wrap="wrap">
                                    <Badge colorScheme="purple" variant="subtle" borderRadius="full">FlowSense AI{answer.model ? ` · ${answer.model}` : ''}</Badge>
                                    {Number.isFinite(Number(answer.duration_ms)) && <Text fontSize="2xs" color={muted}>{(Number(answer.duration_ms) / 1000).toFixed(1)} s</Text>}
                                    {answer.cached && <Badge variant="outline" borderRadius="full" fontSize="2xs">cached</Badge>}
                                    {answer.truncated && <Badge colorScheme="orange" variant="outline" borderRadius="full" fontSize="2xs">shortened</Badge>}
                                    <Box flex={1} />
                                    <Tooltip label={hasCopied ? 'Copied' : 'Copy answer'} hasArrow>
                                        <IconButton size="xs" variant="ghost" icon={<Icon as={MdContentCopy} />} aria-label="Copy answer" onClick={onCopy} />
                                    </Tooltip>
                                </HStack>
                            </Box>
                        )}
                    </Box>
                </VStack>
            </Collapse>
        </Box>
    );
};

FlowSenseAIExplainer.propTypes = {
    module: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    facts: PropTypes.object,
    findings: PropTypes.arrayOf(PropTypes.shape({ severity: PropTypes.string, text: PropTypes.string, evidence: PropTypes.string })),
    questions: PropTypes.arrayOf(PropTypes.string),
    defaultQuestion: PropTypes.string,
    defaultOpen: PropTypes.bool,
    compact: PropTypes.bool,
    isLoading: PropTypes.bool,
    autoAsk: PropTypes.bool,
};

export default memo(FlowSenseAIExplainer);
